import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import { BodySmall, LabelSmallBold } from 'styles/typography';

import { TASK_REASON_LABELS, TaskReason } from './taskCardCopy';
import { TaskFilters, TaskSort } from './taskCards';

interface Props {
  sort: TaskSort;
  onSortChange: (sort: TaskSort) => void;
  filters: TaskFilters;
  onFiltersChange: (filters: TaskFilters) => void;
  // Only the axes actually present in the current result set are offered — a filter for a
  // portal this client has never seen is a dead control.
  sourceSystemOptions: { value: string; label: string }[];
  reasonOptions: TaskReason[];
}

const SORT_OPTIONS: { value: TaskSort; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
];

function toggle<T>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];
}

export default function TasksToolbar({
  sort,
  onSortChange,
  filters,
  onFiltersChange,
  sourceSystemOptions,
  reasonOptions,
}: Props) {
  const [open, setOpen] = useState(false);
  const activeCount = filters.sourceSystems.length + filters.reasons.length;

  return (
    <Wrapper>
      <Bar>
        <Controls>
          <Control>
            <ControlLabel htmlFor='task-sort'>Sort By</ControlLabel>
            <Select
              id='task-sort'
              value={sort}
              onChange={(event) => onSortChange(event.target.value as TaskSort)}
              data-testid='task-sort'>
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Control>
          <Button variant='ghost' size='small' onClick={() => setOpen(!open)} data-testid='task-filters-toggle'>
            Filters{activeCount > 0 ? ` (${activeCount})` : ''}
          </Button>
        </Controls>
      </Bar>
      {open && (
        <Panel data-testid='task-filters-panel'>
          {sourceSystemOptions.length > 0 && (
            <Group>
              <GroupTitle>Source system</GroupTitle>
              {sourceSystemOptions.map((option) => (
                <Choice key={option.value}>
                  <input
                    type='checkbox'
                    checked={filters.sourceSystems.includes(option.value)}
                    onChange={() =>
                      onFiltersChange({ ...filters, sourceSystems: toggle(filters.sourceSystems, option.value) })
                    }
                  />
                  {option.label}
                </Choice>
              ))}
            </Group>
          )}
          {reasonOptions.length > 0 && (
            <Group>
              <GroupTitle>Task type</GroupTitle>
              {reasonOptions.map((reason) => (
                <Choice key={reason}>
                  <input
                    type='checkbox'
                    checked={filters.reasons.includes(reason)}
                    onChange={() => onFiltersChange({ ...filters, reasons: toggle(filters.reasons, reason) })}
                  />
                  {TASK_REASON_LABELS[reason]}
                </Choice>
              ))}
            </Group>
          )}
          {activeCount > 0 && (
            <Button
              variant='ghost'
              size='small'
              onClick={() => onFiltersChange({ sourceSystems: [], reasons: [] })}
              data-testid='task-filters-clear'>
              Clear filters
            </Button>
          )}
        </Panel>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Control = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ControlLabel = styled(LabelSmallBold)`
  color: ${({ theme }) => theme.colors.black75};
`;

const Select = styled.select`
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 14px;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.black10};
  background-color: ${({ theme }) => theme.colors.white};
`;

const Panel = styled.div`
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
  padding: 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.black10};
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const GroupTitle = styled(LabelSmallBold)`
  color: ${({ theme }) => theme.colors.black75};
`;

const Choice = styled(BodySmall).attrs({ as: 'label' })`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;
